// @ts-nocheck
import { api } from '../api.js';
import { getState } from '../state.js';
import { navigate } from '../router.js';
import { upgradeToSearchSelect } from '../components/searchSelect.js';
import { attachConceptAutocomplete } from '../components/conceptAutocomplete.js';

function makeCollapsible(panel) {
  for (const section of panel.querySelectorAll('.admin-section')) {
    const title = section.querySelector('.admin-section__title');
    if (!title || section.querySelector('.admin-section__body')) continue;
    const body = document.createElement('div');
    body.className = 'admin-section__body';
    const inner = document.createElement('div');
    while (title.nextSibling) inner.appendChild(title.nextSibling);
    body.appendChild(inner);
    section.appendChild(body);
    title.addEventListener('click', () => section.classList.toggle('admin-section--collapsed'));
  }
}

export async function renderAdminView(container) {
  const { user } = getState();
  if (!user || !user.is_admin) {
    navigate('/graph');
    return;
  }

  container.innerHTML = '<div class="view"><div class="loading">loading admin...</div></div>';

  try {
    const [concepts, relationTypes, tags] = await Promise.all([
      api.getConcepts(),
      api.getRelationTypes(),
      api.getTags(),
    ]);

    container.innerHTML = '';
    const view = document.createElement('div');
    view.className = 'view';

    view.innerHTML = `
      <div class="view__header">
        <h1 class="view__title">Admin</h1>
        <p class="view__subtitle">Manage the knowledge network.</p>
      </div>
      <div class="admin-tabs">
        <button class="admin-tab admin-tab--active" data-tab="dashboard">Dashboard</button>
        <button class="admin-tab" data-tab="concepts">Concepts</button>
        <button class="admin-tab" data-tab="crosslinks">Crosslinks</button>
        <button class="admin-tab" data-tab="tags">Tags</button>
        <button class="admin-tab" data-tab="translations">Translations</button>
        <button class="admin-tab" data-tab="flows">Flows</button>
        <button class="admin-tab" data-tab="analytics">Analytics</button>
      </div>
      <div class="admin-tab-panels">
        <div class="admin-tab-panel admin-tab-panel--active" data-panel="dashboard"></div>
        <div class="admin-tab-panel" data-panel="concepts"></div>
        <div class="admin-tab-panel" data-panel="crosslinks"></div>
        <div class="admin-tab-panel" data-panel="tags"></div>
        <div class="admin-tab-panel" data-panel="translations"></div>
        <div class="admin-tab-panel" data-panel="flows"></div>
        <div class="admin-tab-panel" data-panel="analytics"></div>
      </div>
    `;

    container.appendChild(view);

    // --- Tab switching ---
    const tabs = view.querySelectorAll('.admin-tab');
    const panels = view.querySelectorAll('.admin-tab-panel');
    const validTabs = new Set([...tabs].map(t => t.dataset.tab));

    function activateTab(name) {
      for (const t of tabs) t.classList.remove('admin-tab--active');
      for (const p of panels) p.classList.remove('admin-tab-panel--active');
      const tab = view.querySelector(`.admin-tab[data-tab="${name}"]`);
      if (tab) tab.classList.add('admin-tab--active');
      const panel = view.querySelector(`[data-panel="${name}"]`);
      if (panel) panel.classList.add('admin-tab-panel--active');
    }

    // Restore tab from URL hash
    const hashTab = window.location.hash.slice(1);
    if (hashTab && validTabs.has(hashTab)) {
      activateTab(hashTab);
    }

    for (const tab of tabs) {
      tab.addEventListener('click', () => {
        activateTab(tab.dataset.tab);
        history.replaceState({}, '', `/admin#${tab.dataset.tab}`);
      });
    }

    const dashboardPanel = view.querySelector('[data-panel="dashboard"]');
    const conceptsPanel = view.querySelector('[data-panel="concepts"]');
    const crosslinksPanel = view.querySelector('[data-panel="crosslinks"]');
    const tagsPanel = view.querySelector('[data-panel="tags"]');
    const translationsPanel = view.querySelector('[data-panel="translations"]');
    const flowsPanel = view.querySelector('[data-panel="flows"]');
    const analyticsPanel = view.querySelector('[data-panel="analytics"]');

    // =====================================================================
    // DASHBOARD TAB
    // =====================================================================
    renderDashboardTab(dashboardPanel, concepts, relationTypes);

    // =====================================================================
    // CONCEPTS TAB
    // =====================================================================
    renderConceptsTab(conceptsPanel, concepts, tags, view);

    // =====================================================================
    // CROSSLINKS TAB
    // =====================================================================
    renderCrosslinksTab(crosslinksPanel, concepts, relationTypes, view);

    // =====================================================================
    // TAGS TAB
    // =====================================================================
    renderTagsTab(tagsPanel, tags, concepts);

    // =====================================================================
    // TRANSLATIONS TAB
    // =====================================================================
    renderTranslationsTab(translationsPanel, concepts, relationTypes);

    // =====================================================================
    // FLOWS TAB
    // =====================================================================
    renderFlowsTab(flowsPanel, concepts);

    // =====================================================================
    // ANALYTICS TAB
    // =====================================================================
    renderAnalyticsTab(analyticsPanel);

  } catch (err) {
    container.innerHTML = `<div class="view"><p class="form-error">Failed to load admin: ${err.message}</p></div>`;
  }
}

// =====================================================================
// Dashboard tab
// =====================================================================
async function renderDashboardTab(panel, concepts, relationTypes) {
  panel.innerHTML = '<div class="loading">Loading dashboard...</div>';

  try {
    // Fetch additional data needed for stats
    const [crosslinks, untranslatedConcepts, untranslatedRelationTypes] = await Promise.all([
      api.getCrosslinks(),
      api.getUntranslatedConcepts('zh-CN'), // Hardcoded to zh-CN for now
      api.getUntranslatedRelationTypes('zh-CN'),
    ]);

    const totalConcepts = concepts.length;
    const totalCrosslinks = crosslinks.length;
    const totalLinkTypes = relationTypes.length;
    const density = totalConcepts > 0 ? (totalCrosslinks / totalConcepts).toFixed(2) : 0;
    const untranslatedConceptCount = untranslatedConcepts.length;
    const untranslatedLinkTypeCount = untranslatedRelationTypes.length;

    panel.innerHTML = `
      <div class="admin-section">
        <h2 class="admin-section__title">Network Statistics</h2>
        <div class="dashboard-stats">
          <div class="dashboard-stat">
            <div class="dashboard-stat__value">${totalConcepts}</div>
            <div class="dashboard-stat__label">Concepts</div>
          </div>
          <div class="dashboard-stat">
            <div class="dashboard-stat__value">${totalCrosslinks}</div>
            <div class="dashboard-stat__label">Crosslinks</div>
          </div>
          <div class="dashboard-stat">
            <div class="dashboard-stat__value">${density}</div>
            <div class="dashboard-stat__label">Density</div>
          </div>
          <div class="dashboard-stat">
            <div class="dashboard-stat__value">${totalLinkTypes}</div>
            <div class="dashboard-stat__label">Link Types</div>
          </div>
        </div>
      </div>

      <div class="admin-section">
        <h2 class="admin-section__title">Translation Status (zh-CN)</h2>
        <div class="dashboard-stats">
          <div class="dashboard-stat">
            <div class="dashboard-stat__value dashboard-stat__value--warn">${untranslatedConceptCount}</div>
            <div class="dashboard-stat__label">Untranslated Concepts</div>
            <div class="dashboard-stat__hint">${totalConcepts > 0 ? Math.round((1 - untranslatedConceptCount / totalConcepts) * 100) : 0}% translated</div>
          </div>
          <div class="dashboard-stat">
            <div class="dashboard-stat__value dashboard-stat__value--warn">${untranslatedLinkTypeCount}</div>
            <div class="dashboard-stat__label">Untranslated Link Types</div>
            <div class="dashboard-stat__hint">${totalLinkTypes > 0 ? Math.round((1 - untranslatedLinkTypeCount / totalLinkTypes) * 100) : 0}% translated</div>
          </div>
        </div>
      </div>

      <div class="admin-section">
        <h2 class="admin-section__title">Quick Actions</h2>
        <div class="dashboard-actions">
          <button class="btn btn--signal" data-action="add-concept">Add Concept</button>
          <button class="btn btn--signal" data-action="add-crosslink">Add Crosslink</button>
          <button class="btn btn--signal" data-action="translate">Bulk Translate</button>
        </div>
      </div>
    `;

    // Add event listeners for quick actions
    const addConceptBtn = panel.querySelector('[data-action="add-concept"]');
    const addCrosslinkBtn = panel.querySelector('[data-action="add-crosslink"]');
    const translateBtn = panel.querySelector('[data-action="translate"]');

    addConceptBtn.addEventListener('click', () => {
      // Switch to concepts tab
      const conceptsTab = document.querySelector('[data-tab="concepts"]');
      conceptsTab.click();
    });

    addCrosslinkBtn.addEventListener('click', () => {
      // Switch to crosslinks tab
      const crosslinksTab = document.querySelector('[data-tab="crosslinks"]');
      crosslinksTab.click();
    });

    translateBtn.addEventListener('click', () => {
      // Switch to translations tab
      const translationsTab = document.querySelector('[data-tab="translations"]');
      translationsTab.click();
    });

    // Data tools — export always available, import only in dev mode
    const isDev = await api.devStatus().then(() => true).catch(() => false);
    const dataSection = document.createElement('div');
    dataSection.className = 'admin-section';
    dataSection.innerHTML = `
      <h2 class="admin-section__title">Data Tools</h2>
      <p class="admin-section__desc">Export the entire knowledge graph as JSON, Obsidian vault, or a standalone HTML file.${isDev ? ' Import is available in dev environments.' : ''}</p>
      <div class="dashboard-actions">
        <button class="btn btn--signal" id="data-export-btn">Export JSON</button>
        <button class="btn btn--signal" id="data-export-obsidian-btn">Export to Obsidian</button>
        <button class="btn btn--signal" id="data-export-standalone-btn">Export Standalone HTML</button>
        ${isDev ? `<label class="btn btn--signal" style="cursor: pointer;">
          Import Data
          <input type="file" accept=".json" id="data-import-input" style="display: none;">
        </label>` : ''}
      </div>
      <div id="data-feedback"></div>
    `;
    panel.appendChild(dataSection);

    document.getElementById('data-export-btn').addEventListener('click', async () => {
      const feedback = document.getElementById('data-feedback');
      try {
        feedback.textContent = 'Exporting...';
        feedback.className = '';
        const exportData = await api.adminExport();
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `crosslink-export-${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
        feedback.textContent = `Exported ${exportData.meta.stats.concepts} concepts, ${exportData.meta.stats.crosslinks} crosslinks`;
        feedback.className = 'admin-section__feedback admin-success';
      } catch (err) {
        feedback.textContent = err.message;
        feedback.className = 'admin-section__feedback form-error';
      }
    });

    document.getElementById('data-export-obsidian-btn').addEventListener('click', async () => {
      const feedback = document.getElementById('data-feedback');
      try {
        feedback.textContent = 'Generating Obsidian vault...';
        feedback.className = '';
        await api.adminExportObsidian();
        feedback.textContent = 'Obsidian vault exported successfully';
        feedback.className = 'admin-section__feedback admin-success';
      } catch (err) {
        feedback.textContent = err.message;
        feedback.className = 'admin-section__feedback form-error';
      }
    });

    document.getElementById('data-export-standalone-btn').addEventListener('click', async () => {
      const feedback = document.getElementById('data-feedback');
      try {
        feedback.textContent = 'Building standalone HTML (this may take a moment)...';
        feedback.className = '';
        await api.adminExportStandalone();
        feedback.textContent = 'Standalone HTML exported successfully';
        feedback.className = 'admin-section__feedback admin-success';
      } catch (err) {
        feedback.textContent = err.message;
        feedback.className = 'admin-section__feedback form-error';
      }
    });

    if (isDev) {
      document.getElementById('data-import-input').addEventListener('change', async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const feedback = document.getElementById('data-feedback');
        try {
          feedback.textContent = 'Reading file...';
          feedback.className = '';
          const text = await file.text();
          const data = JSON.parse(text);
          if (!confirm(`Import ${data.concepts?.length || 0} concepts, ${data.crosslinks?.length || 0} crosslinks, ${data.relationTypes?.length || 0} relation types, ${data.tags?.length || 0} tags? This will REPLACE all existing content data.`)) {
            feedback.textContent = 'Import cancelled';
            feedback.className = 'admin-section__feedback';
            return;
          }
          feedback.textContent = 'Importing...';
          const result = await api.devImport(data);
          feedback.textContent = `Imported: ${result.imported.concepts} concepts, ${result.imported.crosslinks} crosslinks, ${result.imported.relationTypes} relation types, ${result.imported.tags} tags`;
          feedback.className = 'admin-section__feedback admin-success';
          setTimeout(() => window.location.reload(), 1500);
        } catch (err) {
          feedback.textContent = err.message;
          feedback.className = 'admin-section__feedback form-error';
        }
        e.target.value = '';
      });
    }

    makeCollapsible(panel);

  } catch (err) {
    panel.innerHTML = `<div class="admin-section"><div class="form-error">Failed to load dashboard: ${err.message}</div></div>`;
  }
}

// =====================================================================
// Concepts tab
// =====================================================================
function renderConceptsTab(panel, concepts, tags, view) {
  // --- Add Concept ---
  const addConceptSection = document.createElement('div');
  addConceptSection.className = 'admin-section';
  addConceptSection.innerHTML = `
    <h2 class="admin-section__title">Add Concept</h2>
    <form id="add-concept-form">
      <div class="form-group">
        <label class="label">title</label>
        <input class="input" type="text" name="title" placeholder="My Concept" required>
      </div>
      <div class="form-group">
        <label class="label">slug</label>
        <input class="input" type="text" name="slug" placeholder="my-concept" required>
      </div>
      <div class="form-group">
        <label class="label label--optional">summary</label>
        <textarea class="input admin-textarea" name="summary" placeholder="A brief description..."></textarea>
      </div>
      <div class="form-group">
        <label class="label label--optional">date</label>
        <input class="input" type="text" name="date_value" placeholder="e.g. 1993, 199308, -1999" style="max-width: 200px;">
      </div>
      <div class="form-group">
        <label class="label label--optional">tags</label>
        <div id="add-concept-tags" class="tag-picker">
          ${tags.map(t => `
            <label class="tag-picker__option" style="--tag-color: ${t.color};">
              <input type="checkbox" value="${t.id}" data-name="${t.name}" data-color="${t.color}">
              <span class="tag-picker__label">${t.name}</span>
            </label>
          `).join('')}
        </div>
      </div>
      <div class="admin-section__feedback" id="add-concept-feedback"></div>
      <button type="submit" class="btn btn--signal btn--small">Add concept</button>
    </form>
  `;
  panel.appendChild(addConceptSection);

  // --- Edit Concept ---
  const editConceptSection = document.createElement('div');
  editConceptSection.className = 'admin-section';
  editConceptSection.innerHTML = `
    <h2 class="admin-section__title">Edit Concept</h2>
    <form id="edit-concept-form">
      <div class="form-group">
        <label class="label">concept</label>
        <select class="admin-select" id="edit-concept-select">
          <option value="">select a concept...</option>
          ${concepts.map(c => `<option value="${c.slug}" data-slug="${c.slug}" data-aliases="${(c.aliases||[]).join('|')}">${c.title}</option>`).join('')}
        </select>
      </div>
      <div class="form-group">
        <label class="label">slug</label>
        <input class="input" type="text" name="slug" id="edit-slug">
      </div>
      <div class="form-group">
        <label class="label">title</label>
        <input class="input" type="text" name="title" id="edit-title">
      </div>
      <div class="form-group">
        <label class="label">summary</label>
        <textarea class="input admin-textarea" name="summary" id="edit-summary"></textarea>
      </div>
      <div class="form-group">
        <label class="label label--optional">date</label>
        <input class="input" type="text" name="date_value" id="edit-date-value" placeholder="e.g. 1993, 199308, -1999" style="max-width: 200px;">
      </div>
      <div class="form-group admin-image-upload">
        <label class="label label--optional">image</label>
        <img id="concept-image-preview" class="admin-image-upload__preview" style="display:none;" alt="">
        <div style="display: flex; gap: 0.5rem; align-items: center;">
          <input type="file" id="concept-image-input" accept="image/jpeg,image/png,image/webp,image/gif" style="font-size: 0.8rem;">
          <button type="button" class="btn btn--small" id="concept-image-remove" style="display:none; background: var(--dim); color: var(--chalk); font-size: 0.75rem;">Remove</button>
        </div>
        <div class="admin-section__feedback" id="concept-image-feedback" style="margin-top: 0.25rem;"></div>
      </div>
      <div class="admin-section__feedback" id="edit-concept-feedback"></div>
      <div style="display: flex; gap: 0.5rem; align-items: center;">
        <button type="submit" class="btn btn--signal btn--small">Save changes</button>
        <button type="button" class="btn btn--small" id="delete-concept-btn" style="background: var(--dim); color: var(--chalk);">Delete</button>
      </div>
    </form>
  `;
  panel.appendChild(editConceptSection);

  // --- Aliases ---
  const aliasSection = document.createElement('div');
  aliasSection.className = 'admin-section';
  aliasSection.innerHTML = `
    <h2 class="admin-section__title">Aliases</h2>
    <div class="form-group">
      <label class="label">concept</label>
      <select class="admin-select" id="alias-concept-select">
        <option value="">select a concept...</option>
        ${concepts.map(c => `<option value="${c.slug}" data-slug="${c.slug}" data-aliases="${(c.aliases||[]).join('|')}">${c.title}</option>`).join('')}
      </select>
    </div>
    <div id="alias-list" style="margin: 0.75rem 0;"></div>
    <div class="form-group" style="display: flex; gap: 0.5rem; align-items: flex-end;">
      <div style="flex: 1;">
        <label class="label">new alias</label>
        <input class="input" type="text" id="alias-input" placeholder="e.g. ML">
      </div>
      <button class="btn btn--signal btn--small" id="add-alias-btn">Add</button>
    </div>
    <div class="admin-section__feedback" id="alias-feedback"></div>
  `;
  panel.appendChild(aliasSection);

  // --- Event handlers ---

  function showFeedback(id, message, success) {
    const el = document.getElementById(id);
    el.textContent = message;
    el.className = `admin-section__feedback ${success ? 'admin-success' : 'form-error'}`;
  }

  // Auto-fill slug from title
  const addForm = document.getElementById('add-concept-form');
  const slugInput = addForm.querySelector('input[name="slug"]');
  const titleInput = addForm.querySelector('input[name="title"]');
  let slugManuallyEdited = false;

  slugInput.addEventListener('input', () => { slugManuallyEdited = true; });

  titleInput.addEventListener('input', () => {
    if (slugManuallyEdited) return;
    slugInput.value = titleInput.value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    // Clear feedback when user starts typing
    document.getElementById('add-concept-feedback').textContent = '';
  });

  addForm.addEventListener('reset', () => { slugManuallyEdited = false; });

  // Add Concept
  document.getElementById('add-concept-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const form = e.target;
    const slug = form.slug.value.trim();
    const title = form.title.value.trim();
    const summary = form.summary.value.trim();
    const date_value = form.date_value.value.trim() || null;
    try {
      await api.createConcept({ slug, title, summary, date_value });
      // Assign selected tags
      const selectedTags = [...document.querySelectorAll('#add-concept-tags input:checked')];
      for (const cb of selectedTags) {
        await api.assignTagToConcept(slug, cb.value);
      }
      const tagCount = selectedTags.length;
      showFeedback('add-concept-feedback', `Created "${title}"${tagCount ? ` with ${tagCount} tag${tagCount > 1 ? 's' : ''}` : ''}`, true);
      form.reset();
      const updated = await api.getConcepts();
      refreshConceptSelects(updated, concepts, view);
    } catch (err) {
      showFeedback('add-concept-feedback', err.message, false);
    }
  });

  // Edit Concept
  const editSelect = document.getElementById('edit-concept-select');
  const editSlug = document.getElementById('edit-slug');
  const editTitle = document.getElementById('edit-title');
  const editSummary = document.getElementById('edit-summary');
  const editDateValue = document.getElementById('edit-date-value');

  const conceptImgPreview = document.getElementById('concept-image-preview');
  const conceptImgInput = document.getElementById('concept-image-input');
  const conceptImgRemove = document.getElementById('concept-image-remove');
  const conceptImgFeedback = document.getElementById('concept-image-feedback');

  function updateConceptImagePreview(concept) {
    if (concept && concept.image) {
      conceptImgPreview.src = `/uploads/${concept.image}`;
      conceptImgPreview.style.display = 'block';
      conceptImgRemove.style.display = 'inline-block';
    } else {
      conceptImgPreview.style.display = 'none';
      conceptImgPreview.src = '';
      conceptImgRemove.style.display = 'none';
    }
    conceptImgInput.value = '';
    conceptImgFeedback.textContent = '';
  }

  editSelect.addEventListener('change', () => {
    const slug = editSelect.value;
    const concept = concepts.find(c => c.slug === slug);
    if (concept) {
      editSlug.value = concept.slug;
      editTitle.value = concept.title;
      editSummary.value = concept.summary || '';
      editDateValue.value = concept.date_value || '';
    } else {
      editSlug.value = '';
      editTitle.value = '';
      editSummary.value = '';
      editDateValue.value = '';
    }
    updateConceptImagePreview(concept);
    document.getElementById('edit-concept-feedback').textContent = '';
  });

  conceptImgInput.addEventListener('change', async () => {
    const slug = editSelect.value;
    if (!slug) return;
    const file = conceptImgInput.files[0];
    if (!file) return;
    try {
      conceptImgFeedback.textContent = 'Uploading...';
      conceptImgFeedback.className = 'admin-section__feedback';
      const result = await api.uploadConceptImage(slug, file);
      const concept = concepts.find(c => c.slug === slug);
      if (concept) concept.image = result.image;
      updateConceptImagePreview(concept);
      conceptImgFeedback.textContent = 'Image uploaded';
      conceptImgFeedback.className = 'admin-section__feedback form-success';
    } catch (err) {
      conceptImgFeedback.textContent = err.message;
      conceptImgFeedback.className = 'admin-section__feedback form-error';
    }
  });

  conceptImgRemove.addEventListener('click', async () => {
    const slug = editSelect.value;
    if (!slug) return;
    try {
      await api.deleteConceptImage(slug);
      const concept = concepts.find(c => c.slug === slug);
      if (concept) concept.image = null;
      updateConceptImagePreview(concept);
      conceptImgFeedback.textContent = 'Image removed';
      conceptImgFeedback.className = 'admin-section__feedback form-success';
    } catch (err) {
      conceptImgFeedback.textContent = err.message;
      conceptImgFeedback.className = 'admin-section__feedback form-error';
    }
  });

  document.getElementById('edit-concept-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const originalSlug = editSelect.value;
    if (!originalSlug) return showFeedback('edit-concept-feedback', 'Select a concept first', false);
    const newSlug = editSlug.value.trim();
    const dateVal = editDateValue.value.trim() || null;
    try {
      await api.updateConcept(originalSlug, { slug: newSlug, title: editTitle.value, summary: editSummary.value, date_value: dateVal });
      showFeedback('edit-concept-feedback', `Updated "${editTitle.value}"`, true);
      const c = concepts.find(c => c.slug === originalSlug);
      if (c) {
        c.slug = newSlug;
        c.title = editTitle.value;
        c.summary = editSummary.value;
      }
      const updated = await api.getConcepts();
      refreshConceptSelects(updated, concepts, view);
      editSelect.value = newSlug;
    } catch (err) {
      showFeedback('edit-concept-feedback', err.message, false);
    }
  });

  // Delete Concept
  document.getElementById('delete-concept-btn').addEventListener('click', async () => {
    const slug = editSelect.value;
    if (!slug) return showFeedback('edit-concept-feedback', 'Select a concept first', false);
    const concept = concepts.find(c => c.slug === slug);
    const name = concept ? concept.title : slug;
    if (!confirm(`Delete "${name}"? This will remove all crosslinks, user activations, and translations for this concept.`)) return;
    try {
      await api.deleteConcept(slug);
      showFeedback('edit-concept-feedback', `Deleted "${name}"`, true);
      editSlug.value = '';
      editTitle.value = '';
      editSummary.value = '';
      const updated = await api.getConcepts();
      refreshConceptSelects(updated, concepts, view);
    } catch (err) {
      showFeedback('edit-concept-feedback', err.message, false);
    }
  });

  // Alias handlers
  const aliasSelect = document.getElementById('alias-concept-select');
  const aliasList = document.getElementById('alias-list');
  const aliasInput = document.getElementById('alias-input');
  const addAliasBtn = document.getElementById('add-alias-btn');

  async function loadAliases(slug) {
    aliasList.innerHTML = '';
    if (!slug) return;
    try {
      const aliases = await api.getConceptAliases(slug);
      if (aliases.length === 0) {
        aliasList.innerHTML = `<span style="color: var(--dim); font-size: 0.85rem;">No aliases yet.</span>`;
        return;
      }
      aliasList.innerHTML = aliases.map(a =>
        `<span class="concept-translations__item" style="display: inline-flex; align-items: center; gap: 0.4rem; margin: 0.2rem 0.25rem;">
          ${a.alias}
          <button class="btn btn--ghost btn--small alias-delete-btn" data-id="${a.id}" style="padding: 0 0.25rem; font-size: 0.75rem; line-height: 1;">&times;</button>
        </span>`
      ).join('');
      aliasList.querySelectorAll('.alias-delete-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          try {
            await api.deleteConceptAlias(slug, btn.dataset.id);
            loadAliases(slug);
          } catch (err) {
            showFeedback('alias-feedback', err.message, false);
          }
        });
      });
    } catch (err) {
      showFeedback('alias-feedback', err.message, false);
    }
  }

  aliasSelect.addEventListener('change', () => {
    loadAliases(aliasSelect.value);
    aliasInput.value = '';
    document.getElementById('alias-feedback').textContent = '';
  });

  addAliasBtn.addEventListener('click', async () => {
    const slug = aliasSelect.value;
    const alias = aliasInput.value.trim();
    if (!slug) return showFeedback('alias-feedback', 'Select a concept first', false);
    if (!alias) return showFeedback('alias-feedback', 'Enter an alias', false);
    try {
      await api.addConceptAlias(slug, alias);
      aliasInput.value = '';
      showFeedback('alias-feedback', `Added "${alias}"`, true);
      loadAliases(slug);
    } catch (err) {
      showFeedback('alias-feedback', err.message, false);
    }
  });

  // Upgrade selects to searchable
  for (const sel of panel.querySelectorAll('select.admin-select')) {
    upgradeToSearchSelect(sel);
  }

  makeCollapsible(panel);
}

// =====================================================================
// Crosslinks tab
// =====================================================================
function renderCrosslinksTab(panel, concepts, relationTypes, view) {
  // --- Add Crosslink (most used, so first) ---
  const clSection = document.createElement('div');
  clSection.className = 'admin-section';
  clSection.innerHTML = `
    <h2 class="admin-section__title">Add Crosslink</h2>
    <form id="add-cl-form">
      <div class="form-group">
        <label class="label">concept a</label>
        <select class="admin-select" name="concept_a" required>
          <option value="">select concept...</option>
          ${concepts.map(c => `<option value="${c.slug}" data-slug="${c.slug}" data-aliases="${(c.aliases||[]).join('|')}">${c.title}</option>`).join('')}
        </select>
      </div>
      <div class="form-group">
        <label class="label label">relation type</label>
        <select class="admin-select" name="relation_type_id">
          <option value="">none</option>
          ${relationTypes.flatMap(rt => [
            `<option value="${rt.id}">${rt.name}</option>`,
            ...(rt.reverse_name ? [`<option value="${rt.id}" data-reverse="1">${rt.reverse_name}</option>`] : [])
          ]).join('')}
        </select>
      </div>
      <div class="form-group">
        <label class="label">concept b</label>
        <select class="admin-select" name="concept_b" required>
          <option value="">select concept...</option>
          ${concepts.map(c => `<option value="${c.slug}" data-slug="${c.slug}" data-aliases="${(c.aliases||[]).join('|')}">${c.title}</option>`).join('')}
        </select>
      </div>
      <div class="form-group">
        <label class="label label--optional">description</label>
        <textarea class="input admin-textarea" name="description" placeholder="Explain how these concepts connect..." rows="2"></textarea>
      </div>
      <div class="form-group">
        <label class="label label--optional">date</label>
        <input class="input" type="text" name="date_value" placeholder="e.g. 1993, 199308, -1999" style="max-width: 200px;">
      </div>
      <div class="admin-section__feedback" id="add-cl-feedback"></div>
      <button type="submit" class="btn btn--signal btn--small">Add crosslink</button>
    </form>
  `;
  panel.appendChild(clSection);

  // --- Add Relation Type ---
  const addRtSection = document.createElement('div');
  addRtSection.className = 'admin-section';
  addRtSection.innerHTML = `
    <h2 class="admin-section__title">Add Relation Type</h2>
    <div class="admin-tag-list" id="rt-list"></div>
    <form id="add-rt-form">
      <div class="form-group">
        <label class="label">name</label>
        <input class="input" type="text" name="name" placeholder="enables" required>
      </div>
      <div class="form-group">
        <label class="label label--optional">reverse name</label>
        <input class="input" type="text" name="reverse_name" placeholder="is enabled by">
      </div>
      <div class="form-group" style="display: flex; gap: 1.5rem; flex-wrap: wrap;">
        <label style="display: flex; align-items: center; gap: 0.35rem; font-size: 0.875rem; color: var(--chalk);">
          <input type="checkbox" name="is_hierarchical">
          hierarchical
        </label>
        <label style="display: flex; align-items: center; gap: 0.35rem; font-size: 0.875rem; color: var(--dim);">
          <input type="checkbox" name="hierarchy_reversed">
          name = child &rarr; parent (e.g. "is a type of")
        </label>
      </div>
      <div class="admin-section__feedback" id="add-rt-feedback"></div>
      <button type="submit" class="btn btn--signal btn--small">Add type</button>
    </form>
  `;
  panel.appendChild(addRtSection);
  refreshRtList(relationTypes);

  // Fetch crosslink usage counts for relation types
  let rtUsageCounts = new Map();
  api.getCrosslinks().then(crosslinks => {
    rtUsageCounts = new Map();
    for (const rt of relationTypes) rtUsageCounts.set(rt.id, 0);
    for (const cl of crosslinks) {
      if (cl.relation_type_id && rtUsageCounts.has(cl.relation_type_id)) {
        rtUsageCounts.set(cl.relation_type_id, rtUsageCounts.get(cl.relation_type_id) + 1);
      }
    }
    refreshRtList(relationTypes, rtUsageCounts);
  }).catch(() => {});

  // --- Edit Relation Type ---
  const editRtSection = document.createElement('div');
  editRtSection.className = 'admin-section';
  editRtSection.innerHTML = `
    <h2 class="admin-section__title">Edit Relation Type</h2>
    <form id="edit-rt-form">
      <div class="form-group">
        <label class="label">relation type</label>
        <select class="admin-select" id="edit-rt-select">
          <option value="">select a relation type...</option>
          ${relationTypes.map(rt => `<option value="${rt.id}">${rt.name}${rt.reverse_name ? ` / ${rt.reverse_name}` : ''}</option>`).join('')}
        </select>
      </div>
      <div class="form-group">
        <label class="label">name</label>
        <input class="input" type="text" name="name" id="edit-rt-name">
      </div>
      <div class="form-group">
        <label class="label label--optional">reverse name</label>
        <input class="input" type="text" name="reverse_name" id="edit-rt-reverse-name">
      </div>
      <div class="form-group" style="display: flex; gap: 1.5rem; flex-wrap: wrap;">
        <label style="display: flex; align-items: center; gap: 0.35rem; font-size: 0.875rem; color: var(--chalk);">
          <input type="checkbox" name="is_hierarchical" id="edit-rt-hierarchical">
          hierarchical
        </label>
        <label style="display: flex; align-items: center; gap: 0.35rem; font-size: 0.875rem; color: var(--dim);">
          <input type="checkbox" name="hierarchy_reversed" id="edit-rt-hierarchy-reversed">
          name = child &rarr; parent (e.g. "is a type of")
        </label>
      </div>
      <div class="admin-section__feedback" id="edit-rt-feedback"></div>
      <button type="submit" class="btn btn--signal btn--small">Save changes</button>
      <button type="button" class="btn btn--danger btn--small" id="delete-rt-btn" style="display: none;">Delete type</button>
    </form>
  `;
  panel.appendChild(editRtSection);

  // --- Edit Crosslinks ---
  const editClSection = document.createElement('div');
  editClSection.className = 'admin-section';
  editClSection.innerHTML = `
    <h2 class="admin-section__title">Edit Crosslinks</h2>
    <p class="admin-section__desc">Look up a concept to view and edit its crosslinks.</p>
    <div class="form-group">
      <label class="label">concept</label>
      <select class="admin-select" id="edit-cl-concept-select">
        <option value="">select concept...</option>
        ${concepts.map(c => `<option value="${c.slug}" data-slug="${c.slug}" data-aliases="${(c.aliases||[]).join('|')}">${c.title}</option>`).join('')}
      </select>
    </div>
    <div id="crosslinks-list"></div>
  `;
  panel.appendChild(editClSection);

  // --- Filter by Relation Type ---
  const filterRtSection = document.createElement('div');
  filterRtSection.className = 'admin-section';
  filterRtSection.innerHTML = `
    <h2 class="admin-section__title">Filter by Relation Type</h2>
    <p class="admin-section__desc">Browse all crosslinks of a specific relation type.</p>
    <div class="form-group">
      <label class="label">relation type</label>
      <select class="admin-select" id="filter-rt-select">
        <option value="">select relation type...</option>
        <option value="__none__">no type assigned</option>
        ${relationTypes.map(rt => `<option value="${rt.id}">${rt.name}${rt.reverse_name ? ` / ${rt.reverse_name}` : ''}</option>`).join('')}
      </select>
    </div>
    <div id="filter-rt-list"></div>
  `;
  panel.appendChild(filterRtSection);

  // --- Unlinked Concepts ---
  const unlinkedSection = document.createElement('div');
  unlinkedSection.className = 'admin-section';
  unlinkedSection.innerHTML = `
    <h2 class="admin-section__title">Unlinked Concepts</h2>
    <p class="admin-section__desc">Concepts with no crosslinks. Quickly add connections for them.</p>
    <div id="unlinked-list"><div class="loading">loading...</div></div>
  `;
  panel.appendChild(unlinkedSection);

  loadUnlinkedConcepts(concepts, relationTypes);

  // --- Upgrade selects to searchable ---
  for (const sel of panel.querySelectorAll('select.admin-select')) {
    // Skip language selects (few options, no need for search)
    if (sel.name === 'lang' || sel.id === 'bulk-lang-select') continue;
    upgradeToSearchSelect(sel);
  }

  // --- Event handlers ---

  function showFeedback(id, message, success) {
    const el = document.getElementById(id);
    el.textContent = message;
    el.className = `admin-section__feedback ${success ? 'admin-success' : 'form-error'}`;
  }

  // Edit Crosslinks: concept lookup
  const editClConceptSelect = document.getElementById('edit-cl-concept-select');
  editClConceptSelect.addEventListener('change', () => {
    const slug = editClConceptSelect.value;
    if (slug) {
      loadConceptCrosslinks(slug, relationTypes);
    } else {
      document.getElementById('crosslinks-list').innerHTML = '';
    }
  });

  // Filter by Relation Type
  const filterRtSelect = document.getElementById('filter-rt-select');
  filterRtSelect.addEventListener('change', () => {
    const val = filterRtSelect.value;
    if (val) {
      loadCrosslinksByRelationType(val, relationTypes);
    } else {
      document.getElementById('filter-rt-list').innerHTML = '';
    }
  });

  // Clear feedback when user starts typing in forms
  const rtForm = document.getElementById('add-rt-form');
  rtForm.querySelector('input[name="name"]').addEventListener('input', () => {
    document.getElementById('add-rt-feedback').textContent = '';
  });

  const clForm = document.getElementById('add-cl-form');
  clForm.querySelector('select[name="concept_a"]').addEventListener('change', () => {
    document.getElementById('add-cl-feedback').textContent = '';
  });

  // Edit Relation Type
  const editRtSelect = document.getElementById('edit-rt-select');
  const editRtName = document.getElementById('edit-rt-name');
  const editRtReverseName = document.getElementById('edit-rt-reverse-name');
  const editRtHierarchical = document.getElementById('edit-rt-hierarchical');
  const editRtHierarchyReversed = document.getElementById('edit-rt-hierarchy-reversed');
  const deleteRtBtn = document.getElementById('delete-rt-btn');

  editRtSelect.addEventListener('change', () => {
    const rtId = editRtSelect.value;
    const rt = relationTypes.find(r => r.id == rtId);
    if (rt) {
      editRtName.value = rt.name;
      editRtReverseName.value = rt.reverse_name || '';
      editRtHierarchical.checked = !!rt.is_hierarchical;
      editRtHierarchyReversed.checked = !!rt.hierarchy_reversed;
      deleteRtBtn.style.display = 'inline-block';
    } else {
      editRtName.value = '';
      editRtReverseName.value = '';
      editRtHierarchical.checked = false;
      editRtHierarchyReversed.checked = false;
      deleteRtBtn.style.display = 'none';
    }
    // Clear feedback when switching relation types
    document.getElementById('edit-rt-feedback').textContent = '';
  });

  document.getElementById('edit-rt-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const rtId = editRtSelect.value;
    if (!rtId) return showFeedback('edit-rt-feedback', 'Select a relation type first', false);
    const name = editRtName.value.trim();
    const reverseName = editRtReverseName.value.trim() || undefined;
    const isHierarchical = editRtHierarchical.checked ? 1 : 0;
    const hierarchyReversed = editRtHierarchyReversed.checked ? 1 : 0;
    try {
      await api.updateRelationType(rtId, { name, reverse_name: reverseName, is_hierarchical: isHierarchical, hierarchy_reversed: hierarchyReversed });
      showFeedback('edit-rt-feedback', `Updated "${name}"`, true);
      const rt = relationTypes.find(r => r.id == rtId);
      if (rt) {
        rt.name = name;
        rt.reverse_name = reverseName;
        rt.is_hierarchical = isHierarchical;
        rt.hierarchy_reversed = hierarchyReversed;
      }
      refreshRtSelects(relationTypes, view);
      // Update the select option text
      const option = editRtSelect.querySelector(`option[value="${rtId}"]`);
      if (option) option.textContent = `${name}${reverseName ? ` / ${reverseName}` : ''}`;
    } catch (err) {
      showFeedback('edit-rt-feedback', err.message, false);
    }
  });

  deleteRtBtn.addEventListener('click', async () => {
    const rtId = editRtSelect.value;
    const rt = relationTypes.find(r => r.id == rtId);
    if (!rt) return;
    if (!confirm(`Delete relation type "${rt.name}"${rt.reverse_name ? ` / "${rt.reverse_name}"` : ''}?`)) return;
    try {
      await api.deleteRelationType(rtId);
      const index = relationTypes.findIndex(r => r.id == rtId);
      if (index !== -1) {
        relationTypes.splice(index, 1);
        rtUsageCounts.delete(parseInt(rtId));
      }
      refreshRtList(relationTypes, rtUsageCounts);
      refreshRtSelects(relationTypes, view);
      // Reset the edit form
      editRtSelect.value = '';
      editRtName.value = '';
      editRtReverseName.value = '';
      deleteRtBtn.style.display = 'none';
      showFeedback('edit-rt-feedback', 'Relation type deleted', true);
    } catch (err) {
      showFeedback('edit-rt-feedback', err.message, false);
    }
  });

  // Add Relation Type
  document.getElementById('add-rt-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const form = e.target;
    const name = form.name.value.trim();
    const reverseName = form.reverse_name.value.trim() || undefined;
    const isHierarchical = form.is_hierarchical.checked ? 1 : 0;
    const hierarchyReversed = form.hierarchy_reversed.checked ? 1 : 0;
    try {
      const rt = await api.createRelationType({ name, reverse_name: reverseName, is_hierarchical: isHierarchical, hierarchy_reversed: hierarchyReversed });
      showFeedback('add-rt-feedback', `Created "${name}"${reverseName ? ` / "${reverseName}"` : ''}`, true);
      form.reset();
      relationTypes.push(rt);
      rtUsageCounts.set(rt.id, 0);
      refreshRtList(relationTypes, rtUsageCounts);
      refreshRtSelects(relationTypes, view);
    } catch (err) {
      showFeedback('add-rt-feedback', err.message, false);
    }
  });

  // Add Crosslink
  document.getElementById('add-cl-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const form = e.target;
    let conceptA = form.concept_a.value;
    let conceptB = form.concept_b.value;
    const rtSelect = form.relation_type_id;
    const selectedOption = rtSelect.options[rtSelect.selectedIndex];
    const isReverse = selectedOption && selectedOption.dataset.reverse === '1';
    const rtId = rtSelect.value || null;
    const description = form.description.value.trim() || null;
    const clDateValue = form.date_value.value.trim() || null;
    // If a reverse relation name was selected, flip the concepts
    if (isReverse) {
      [conceptA, conceptB] = [conceptB, conceptA];
    }
    try {
      await api.createCrosslink({ concept_a_slug: conceptA, concept_b_slug: conceptB, relation_type_id: rtId, description, date_value: clDateValue });
      showFeedback('add-cl-feedback', `Crosslink created${isReverse ? ' (reversed)' : ''}`, true);
      form.reset();
      // Refresh unlinked list since this may have linked a concept
      loadUnlinkedConcepts(concepts, relationTypes);
    } catch (err) {
      showFeedback('add-cl-feedback', err.message, false);
    }
  });

  makeCollapsible(panel);
}

async function loadConceptCrosslinks(slug, relationTypes) {
  const listEl = document.getElementById('crosslinks-list');
  if (!listEl) return;

  listEl.innerHTML = '<div class="loading">loading crosslinks...</div>';

  try {
    const crosslinks = await api.getConceptCrosslinks(slug);

    if (crosslinks.length === 0) {
      listEl.innerHTML = '<div class="admin-empty">No crosslinks for this concept.</div>';
      return;
    }

    listEl.innerHTML = `<p class="admin-section__desc" style="margin-bottom: 0.5rem;">${crosslinks.length} crosslink${crosslinks.length !== 1 ? 's' : ''}</p>`;

    const bulkList = document.createElement('div');
    bulkList.className = 'admin-bulk-list';
    listEl.appendChild(bulkList);

    for (const cl of crosslinks) {
      const row = document.createElement('div');
      row.className = 'admin-bulk-row';

      const rtOptions = `<option value="">none</option>${relationTypes.map(rt =>
        `<option value="${rt.id}" ${cl.relation_type_id == rt.id ? 'selected' : ''}>${rt.name}${rt.reverse_name ? ` / ${rt.reverse_name}` : ''}</option>`
      ).join('')}`;

      // Show concepts in the intended direction based on reverse flag
      const displayA = cl.reverse ? cl.concept_b_title : cl.concept_a_title;
      const displayB = cl.reverse ? cl.concept_a_title : cl.concept_b_title;
      cl.concept_a_title = displayA;
      cl.concept_b_title = displayB;

      row.innerHTML = `
        <div class="admin-bulk-row__title">${displayA} &rarr; ${displayB}</div>
        <form class="admin-bulk-row__form" data-id="${cl.id}" style="flex-wrap: wrap;">
          <select class="admin-select admin-bulk-row__select--small" name="relation_type_id" style="width: 160px;">${rtOptions}</select>
          <textarea class="input admin-textarea" name="description" placeholder="Describe this connection..." rows="2" style="flex: 1; min-width: 200px;">${cl.description || ''}</textarea>
          <input class="input" type="text" name="date_value" placeholder="date" value="${cl.date_value || ''}" style="width: 120px;">
          <button type="submit" class="btn btn--signal btn--small">Save</button>
          <button type="button" class="btn btn--small admin-cl-reverse" data-id="${cl.id}">Reverse</button>
          <button type="button" class="btn btn--danger btn--small admin-cl-delete" data-id="${cl.id}">Delete</button>
        </form>
        <div class="admin-cl-image" style="display: flex; align-items: center; gap: 0.5rem; margin-top: 0.25rem;">
          <img class="admin-cl-image__preview" src="${cl.image ? `/uploads/${cl.image}` : ''}" style="max-width: 80px; max-height: 60px; border-radius: 4px; object-fit: cover;${cl.image ? '' : ' display: none;'}" alt="">
          <input type="file" class="admin-cl-image__input" accept="image/jpeg,image/png,image/webp,image/gif" style="font-size: 0.75rem; max-width: 180px;">
          <button type="button" class="btn btn--small admin-cl-image__remove" style="font-size: 0.7rem; padding: 0.15rem 0.4rem; background: var(--dim); color: var(--chalk);${cl.image ? '' : ' display: none;'}">Remove</button>
          <span class="admin-cl-image__status" style="font-size: 0.75rem;"></span>
        </div>
        <div class="admin-section__feedback admin-bulk-row__feedback"></div>
      `;
      bulkList.appendChild(row);

      // Upgrade selects to searchable
      for (const sel of row.querySelectorAll('select.admin-select')) {
        upgradeToSearchSelect(sel);
      }

      row.querySelector('form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const description = e.target.description.value.trim() || null;
        const relation_type_id = e.target.relation_type_id.value || null;
        const date_value = e.target.date_value.value.trim() || null;
        const feedback = row.querySelector('.admin-bulk-row__feedback');
        try {
          await api.updateCrosslink(cl.id, { description, relation_type_id, date_value });
          feedback.textContent = 'Saved';
          feedback.className = 'admin-section__feedback admin-bulk-row__feedback admin-success';
        } catch (err) {
          feedback.textContent = err.message;
          feedback.className = 'admin-section__feedback admin-bulk-row__feedback form-error';
        }
      });

      row.querySelector('.admin-cl-reverse').addEventListener('click', async () => {
        const feedback = row.querySelector('.admin-bulk-row__feedback');
        try {
          const newReverse = cl.reverse ? 0 : 1;
          await api.updateCrosslink(cl.id, { reverse: newReverse });
          cl.reverse = newReverse;
          // Swap the displayed title
          const titleEl = row.querySelector('.admin-bulk-row__title');
          const tmpTitle = cl.concept_a_title;
          cl.concept_a_title = cl.concept_b_title;
          cl.concept_b_title = tmpTitle;
          titleEl.innerHTML = `${cl.concept_a_title} &rarr; ${cl.concept_b_title}`;
          feedback.textContent = 'Reversed';
          feedback.className = 'admin-section__feedback admin-bulk-row__feedback admin-success';
        } catch (err) {
          feedback.textContent = err.message;
          feedback.className = 'admin-section__feedback admin-bulk-row__feedback form-error';
        }
      });

      // Crosslink image upload/remove
      const clImgPreview = row.querySelector('.admin-cl-image__preview');
      const clImgInput = row.querySelector('.admin-cl-image__input');
      const clImgRemove = row.querySelector('.admin-cl-image__remove');
      const clImgStatus = row.querySelector('.admin-cl-image__status');

      clImgInput.addEventListener('change', async () => {
        const file = clImgInput.files[0];
        if (!file) return;
        try {
          clImgStatus.textContent = 'Uploading...';
          const result = await api.uploadCrosslinkImage(cl.id, file);
          cl.image = result.image;
          clImgPreview.src = `/uploads/${result.image}`;
          clImgPreview.style.display = '';
          clImgRemove.style.display = '';
          clImgInput.value = '';
          clImgStatus.textContent = 'Uploaded';
          clImgStatus.style.color = 'var(--acid)';
        } catch (err) {
          clImgStatus.textContent = err.message;
          clImgStatus.style.color = 'var(--signal)';
        }
      });

      clImgRemove.addEventListener('click', async () => {
        try {
          await api.deleteCrosslinkImage(cl.id);
          cl.image = null;
          clImgPreview.style.display = 'none';
          clImgRemove.style.display = 'none';
          clImgInput.value = '';
          clImgStatus.textContent = 'Removed';
          clImgStatus.style.color = 'var(--acid)';
        } catch (err) {
          clImgStatus.textContent = err.message;
          clImgStatus.style.color = 'var(--signal)';
        }
      });

      row.querySelector('.admin-cl-delete').addEventListener('click', async () => {
        if (!confirm(`Delete crosslink "${cl.concept_a_title} \u2192 ${cl.concept_b_title}"?`)) return;
        const feedback = row.querySelector('.admin-bulk-row__feedback');
        try {
          await api.deleteCrosslink(cl.id);
          row.remove();
          // Update count
          const remaining = bulkList.querySelectorAll('.admin-bulk-row').length;
          const desc = listEl.querySelector('.admin-section__desc');
          if (desc) desc.textContent = `${remaining} crosslink${remaining !== 1 ? 's' : ''}`;
          if (remaining === 0) {
            listEl.innerHTML = '<div class="admin-empty">No crosslinks for this concept.</div>';
          }
        } catch (err) {
          feedback.textContent = err.message;
          feedback.className = 'admin-section__feedback admin-bulk-row__feedback form-error';
        }
      });
    }
  } catch (err) {
    listEl.innerHTML = `<div class="form-error">${err.message}</div>`;
  }
}

async function loadCrosslinksByRelationType(filterValue, relationTypes) {
  const listEl = document.getElementById('filter-rt-list');
  if (!listEl) return;

  listEl.innerHTML = '<div class="loading">loading crosslinks...</div>';

  try {
    const allCrosslinks = await api.getCrosslinks();
    const filtered = allCrosslinks.filter(cl => {
      if (filterValue === '__none__') return !cl.relation_type_id;
      return cl.relation_type_id == filterValue;
    });

    if (filtered.length === 0) {
      listEl.innerHTML = '<div class="admin-empty">No crosslinks with this relation type.</div>';
      return;
    }

    listEl.innerHTML = `<p class="admin-section__desc" style="margin-bottom: 0.5rem;">${filtered.length} crosslink${filtered.length !== 1 ? 's' : ''}</p>`;

    const bulkList = document.createElement('div');
    bulkList.className = 'admin-bulk-list';
    listEl.appendChild(bulkList);

    for (const cl of filtered) {
      const row = document.createElement('div');
      row.className = 'admin-bulk-row';

      const displayA = cl.reverse ? cl.concept_b_title : cl.concept_a_title;
      const displayB = cl.reverse ? cl.concept_a_title : cl.concept_b_title;

      const rtLabel = cl.relation_type_name || '';

      row.innerHTML = `
        <div class="admin-bulk-row__title">${displayA} <span style="color: var(--dim);">${rtLabel ? `\u2014 ${rtLabel} \u2014` : '\u2192'}</span> ${displayB}</div>
        ${cl.description ? `<p class="admin-section__desc" style="margin: 0.25rem 0 0;">${cl.description}</p>` : ''}
      `;
      bulkList.appendChild(row);
    }
  } catch (err) {
    listEl.innerHTML = `<div class="form-error">${err.message}</div>`;
  }
}

async function loadUnlinkedConcepts(concepts, relationTypes) {
  const listEl = document.getElementById('unlinked-list');
  if (!listEl) return;

  try {
    const unlinked = await api.getUnlinkedConcepts();

    if (unlinked.length === 0) {
      listEl.innerHTML = '<div class="admin-empty">All concepts have at least one crosslink.</div>';
      return;
    }

    listEl.innerHTML = `<p class="admin-section__desc" style="margin-bottom: 0.5rem;">${unlinked.length} concept${unlinked.length !== 1 ? 's' : ''} with no crosslinks.</p>`;

    const bulkList = document.createElement('div');
    bulkList.className = 'admin-bulk-list';
    listEl.appendChild(bulkList);

    for (const concept of unlinked) {
      const row = document.createElement('div');
      row.className = 'admin-bulk-row';
      row.innerHTML = `
        <div class="admin-bulk-row__title">${concept.title}</div>
        <form class="admin-bulk-row__form" data-slug="${concept.slug}">
          <select class="admin-select admin-bulk-row__select" name="target" required>
            <option value="">link to...</option>
            ${concepts.filter(c => c.slug !== concept.slug).map(c => `<option value="${c.slug}" data-slug="${c.slug}" data-aliases="${(c.aliases||[]).join('|')}">${c.title}</option>`).join('')}
          </select>
          <select class="admin-select admin-bulk-row__select--small" name="relation_type_id">
            <option value="">type</option>
            ${relationTypes.map(rt => `<option value="${rt.id}">${rt.name}</option>`).join('')}
          </select>
          <button type="submit" class="btn btn--signal btn--small">Link</button>
        </form>
        <div class="admin-section__feedback admin-bulk-row__feedback"></div>
      `;
      bulkList.appendChild(row);

      // Upgrade selects to searchable
      for (const sel of row.querySelectorAll('select.admin-select')) {
        upgradeToSearchSelect(sel);
      }

      row.querySelector('form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const target = e.target.target.value;
        const rtId = e.target.relation_type_id.value || null;
        const feedback = row.querySelector('.admin-bulk-row__feedback');
        try {
          await api.createCrosslink({ concept_a_slug: concept.slug, concept_b_slug: target, relation_type_id: rtId });
          feedback.textContent = 'Linked';
          feedback.className = 'admin-section__feedback admin-bulk-row__feedback admin-success';
          row.classList.add('admin-bulk-row--done');
        } catch (err) {
          feedback.textContent = err.message;
          feedback.className = 'admin-section__feedback admin-bulk-row__feedback form-error';
        }
      });
    }
  } catch (err) {
    listEl.innerHTML = `<div class="form-error">${err.message}</div>`;
  }
}

// =====================================================================
// Shared helpers
// =====================================================================
function refreshConceptSelects(updated, concepts, view) {
  concepts.length = 0;
  concepts.push(...updated);
  const options = `<option value="">select concept...</option>${concepts.map(c => `<option value="${c.slug}" data-slug="${c.slug}" data-aliases="${(c.aliases||[]).join('|')}">${c.title}</option>`).join('')}`;
  const editOptions = `<option value="">select a concept...</option>${concepts.map(c => `<option value="${c.slug}" data-slug="${c.slug}" data-aliases="${(c.aliases||[]).join('|')}">${c.title}</option>`).join('')}`;
  const editSelect = document.getElementById('edit-concept-select');
  if (editSelect) {
    editSelect.innerHTML = editOptions;
    const w = editSelect.nextElementSibling;
    if (w && w._refresh) w._refresh();
  }
  // Also update alias concept select if it exists
  const aliasConceptSelect = document.getElementById('alias-concept-select');
  if (aliasConceptSelect) {
    aliasConceptSelect.innerHTML = editOptions;
    const w = aliasConceptSelect.nextElementSibling;
    if (w && w._refresh) w._refresh();
  }
  // Also update translation concept selects if they exist
  const transConceptSelect = document.getElementById('trans-concept-select');
  if (transConceptSelect) {
    transConceptSelect.innerHTML = editOptions;
    const w = transConceptSelect.nextElementSibling;
    if (w && w._refresh) w._refresh();
  }
  const bulkTransConceptList = document.getElementById('bulk-trans-list');
  if (bulkTransConceptList) {
    // Trigger a re-render of the bulk translations panel
    const langSelect = document.getElementById('bulk-lang-select');
    if (langSelect && langSelect.value) langSelect.dispatchEvent(new Event('change'));
  }
  for (const sel of view.querySelectorAll('select[name="concept_a"], select[name="concept_b"]')) {
    sel.innerHTML = options;
    const w = sel.nextElementSibling;
    if (w && w._refresh) w._refresh();
  }
}

function refreshRtList(relationTypes, rtUsageCounts) {
  const list = document.getElementById('rt-list');
  if (list) {
    list.innerHTML = relationTypes.length
      ? relationTypes.map(rt => {
          const count = rtUsageCounts ? (rtUsageCounts.get(rt.id) || 0) : null;
          const unused = count === 0;
          const countLabel = count !== null ? ` <span class="admin-tag__count">${count}</span>` : '';
          return `<span class="admin-tag${unused ? ' admin-tag--unused' : ''}" title="${unused ? 'Not used by any crosslink' : `Used by ${count} crosslink${count !== 1 ? 's' : ''}`}">${rt.name}${rt.reverse_name ? ` / ${rt.reverse_name}` : ''}${countLabel} <button class="admin-tag__delete" data-rt-id="${rt.id}">&times;</button></span>`;
        }).join('')
      : '<span class="admin-empty">no relation types yet</span>';

    for (const btn of list.querySelectorAll('.admin-tag__delete')) {
      btn.addEventListener('click', async () => {
        const rtId = btn.dataset.rtId;
        const rt = relationTypes.find(r => r.id == rtId);
        if (!rt || !confirm(`Delete relation type "${rt.name}"${rt.reverse_name ? ` / "${rt.reverse_name}"` : ''}?`)) return;
        try {
          await api.deleteRelationType(rtId);
          const index = relationTypes.findIndex(r => r.id == rtId);
          if (index !== -1) {
            relationTypes.splice(index, 1);
            if (rtUsageCounts) rtUsageCounts.delete(parseInt(rtId));
          }
          refreshRtList(relationTypes, rtUsageCounts);
          refreshRtSelects(relationTypes, document.querySelector('.view'));
        } catch (err) {
          console.error('Failed to delete relation type:', err);
        }
      });
    }
  }
}

function refreshRtSelects(relationTypes, view) {
  const rtOptions = `<option value="">none</option>${relationTypes.flatMap(rt => [
    `<option value="${rt.id}">${rt.name}</option>`,
    ...(rt.reverse_name ? [`<option value="${rt.id}" data-reverse="1">${rt.reverse_name}</option>`] : [])
  ]).join('')}`;
  for (const sel of view.querySelectorAll('select[name="relation_type_id"]')) {
    sel.innerHTML = rtOptions;
    // Refresh associated search-select wrapper
    const wrapper = sel.nextElementSibling;
    if (wrapper && wrapper._refresh) wrapper._refresh();
  }
  // Also refresh the edit relation type select
  const editRtSelect = document.getElementById('edit-rt-select');
  if (editRtSelect) {
    const editRtOptions = `<option value="">select a relation type...</option>${relationTypes.map(rt => `<option value="${rt.id}">${rt.name}${rt.reverse_name ? ` / ${rt.reverse_name}` : ''}</option>`).join('')}`;
    editRtSelect.innerHTML = editRtOptions;
    const wrapper = editRtSelect.nextElementSibling;
    if (wrapper && wrapper._refresh) wrapper._refresh();
  }
}

// =====================================================================
// Translations tab
// =====================================================================
function renderTranslationsTab(panel, concepts, relationTypes) {
  panel.innerHTML = `
    <div class="admin-section">
      <h2 class="admin-section__title">Concept Translation</h2>
      <div class="form-group">
        <label class="label">concept</label>
        <select class="admin-select" id="trans-concept-select">
          <option value="">select a concept...</option>
          ${concepts.map(c => `<option value="${c.slug}" data-slug="${c.slug}" data-aliases="${(c.aliases||[]).join('|')}">${c.title}</option>`).join('')}
        </select>
      </div>
      <div id="trans-list"></div>
      <div id="trans-form-container" style="display: none;">
        <form id="add-trans-form">
          <div style="display: grid; grid-template-columns: 80px 1fr; gap: 0.75rem; margin-bottom: 0.75rem;">
            <div class="form-group" style="margin-bottom: 0;">
              <label class="label">lang</label>
              <select class="admin-select" name="lang" required>
                <option value="zh-CN" selected>zh-CN</option>
              </select>
            </div>
            <div class="form-group" style="margin-bottom: 0;">
              <label class="label">title</label>
              <input class="input" type="text" name="title" placeholder="Translated title" required>
            </div>
          </div>
          <div class="admin-section__feedback" id="add-trans-feedback"></div>
          <button type="submit" class="btn btn--signal btn--small">Save translation</button>
        </form>
      </div>
    </div>

    <div class="admin-section">
      <h2 class="admin-section__title">Relation Type Translations</h2>
      <div class="form-group">
        <label class="label">relation type</label>
        <select class="admin-select" id="rt-trans-select">
          <option value="">select a relation type...</option>
          ${relationTypes.map(rt => `<option value="${rt.id}">${rt.name}</option>`).join('')}
        </select>
      </div>
      <div id="rt-trans-list"></div>
      <div id="rt-trans-form-container" style="display: none;">
        <form id="add-rt-trans-form">
          <div style="display: grid; grid-template-columns: 80px 1fr 1fr; gap: 0.75rem; margin-bottom: 0.75rem;">
            <div class="form-group" style="margin-bottom: 0;">
              <label class="label">lang</label>
              <select class="admin-select" name="lang" required>
                <option value="zh-CN" selected>zh-CN</option>
              </select>
            </div>
            <div class="form-group" style="margin-bottom: 0;">
              <label class="label">name</label>
              <input class="input" type="text" name="name" placeholder="Translated name" required>
            </div>
            <div class="form-group" style="margin-bottom: 0;">
              <label class="label label--optional">reverse</label>
              <input class="input" type="text" name="reverse_name" placeholder="Translated reverse">
            </div>
          </div>
          <div class="admin-section__feedback" id="add-rt-trans-feedback"></div>
          <button type="submit" class="btn btn--signal btn--small">Save translation</button>
        </form>
      </div>
    </div>

    <div class="admin-section">
      <h2 class="admin-section__title">Bulk Translate</h2>
      <p class="admin-section__desc">Select a language to see all concepts that still need translation.</p>
      <div class="form-group">
        <label class="label">language</label>
        <select class="admin-select" id="bulk-lang-select">
          <option value="">select a language...</option>
          <option value="zh-CN">zh-CN (Chinese)</option>
        </select>
      </div>
      <div id="bulk-trans-list"></div>
    </div>
  `;

  // Upgrade concept and RT selects in translations tab
  for (const sel of panel.querySelectorAll('select.admin-select')) {
    if (sel.name === 'lang' || sel.id === 'bulk-lang-select') continue;
    upgradeToSearchSelect(sel);
  }

  // --- Concept translations ---
  const transSelect = document.getElementById('trans-concept-select');
  const transList = document.getElementById('trans-list');
  const transFormContainer = document.getElementById('trans-form-container');

  async function loadTranslations(slug) {
    if (!slug) {
      transList.innerHTML = '';
      transFormContainer.style.display = 'none';
      return;
    }
    try {
      const translations = await api.getConceptTranslations(slug);
      transList.innerHTML = translations.length
        ? `<div class="admin-tag-list" style="margin-bottom: 1.25rem;">${translations.map(t =>
            `<span class="admin-tag">${t.lang}: ${t.title} <button class="admin-tag__delete" data-lang="${t.lang}">&times;</button></span>`
          ).join('')}</div>`
        : '<div class="admin-empty" style="margin-bottom: 1.25rem;">no translations yet</div>';
      transFormContainer.style.display = 'block';

      for (const btn of transList.querySelectorAll('.admin-tag__delete')) {
        btn.addEventListener('click', async () => {
          await api.deleteConceptTranslation(slug, btn.dataset.lang);
          loadTranslations(slug);
        });
      }
    } catch (err) {
      transList.innerHTML = `<div class="form-error">${err.message}</div>`;
    }
  }

  transSelect.addEventListener('change', () => {
    // Clear feedback when switching concepts
    document.getElementById('add-trans-feedback').textContent = '';
    loadTranslations(transSelect.value);
  });

  document.getElementById('add-trans-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const slug = transSelect.value;
    if (!slug) return showTransFeedback('add-trans-feedback', 'Select a concept first', false);
    const form = e.target;
    const lang = form.lang.value.trim().toLowerCase();
    const title = form.title.value.trim();
    try {
      await api.setConceptTranslation(slug, lang, { title });
      showTransFeedback('add-trans-feedback', `Saved ${lang} translation`, true);
      form.reset();
      loadTranslations(slug);
    } catch (err) {
      showTransFeedback('add-trans-feedback', err.message, false);
    }
  });

  // --- Relation type translations ---
  const rtTransSelect = document.getElementById('rt-trans-select');
  const rtTransList = document.getElementById('rt-trans-list');
  const rtTransFormContainer = document.getElementById('rt-trans-form-container');

  async function loadRtTranslations(id) {
    if (!id) {
      rtTransList.innerHTML = '';
      rtTransFormContainer.style.display = 'none';
      return;
    }
    try {
      const translations = await api.getRelationTypeTranslations(id);
      rtTransList.innerHTML = translations.length
        ? `<div class="admin-tag-list" style="margin-bottom: 1.25rem;">${translations.map(t =>
            `<span class="admin-tag">${t.lang}: ${t.name}${t.reverse_name ? ` / ${t.reverse_name}` : ''} <button class="admin-tag__delete" data-lang="${t.lang}" data-rt-id="${id}">&times;</button></span>`
          ).join('')}</div>`
        : '<div class="admin-empty" style="margin-bottom: 1.25rem;">no translations yet</div>';
      rtTransFormContainer.style.display = 'block';

      for (const btn of rtTransList.querySelectorAll('.admin-tag__delete')) {
        btn.addEventListener('click', async () => {
          await api.deleteRelationTypeTranslation(btn.dataset.rtId, btn.dataset.lang);
          loadRtTranslations(id);
        });
      }
    } catch (err) {
      rtTransList.innerHTML = `<div class="form-error">${err.message}</div>`;
    }
  }

  rtTransSelect.addEventListener('change', () => {
    // Clear feedback when switching relation types
    document.getElementById('add-rt-trans-feedback').textContent = '';
    loadRtTranslations(rtTransSelect.value);
  });

  document.getElementById('add-rt-trans-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const id = rtTransSelect.value;
    if (!id) return showTransFeedback('add-rt-trans-feedback', 'Select a relation type first', false);
    const form = e.target;
    const lang = form.lang.value.trim().toLowerCase();
    const name = form.name.value.trim();
    const reverse_name = form.reverse_name.value.trim() || undefined;
    try {
      await api.setRelationTypeTranslation(id, lang, { name, reverse_name });
      showTransFeedback('add-rt-trans-feedback', `Saved ${lang} translation`, true);
      form.reset();
      loadRtTranslations(id);
    } catch (err) {
      showTransFeedback('add-rt-trans-feedback', err.message, false);
    }
  });

  // --- Bulk Translate ---
  const bulkLangSelect = document.getElementById('bulk-lang-select');
  const bulkTransList = document.getElementById('bulk-trans-list');

  bulkLangSelect.addEventListener('change', async () => {
    const lang = bulkLangSelect.value;
    if (!lang) {
      bulkTransList.innerHTML = '';
      return;
    }
    bulkTransList.innerHTML = '<div class="loading">checking translations...</div>';

    try {
      const [untranslated, untranslatedRts] = await Promise.all([
        api.getUntranslatedConcepts(lang),
        api.getUntranslatedRelationTypes(lang),
      ]);

      const total = untranslated.length + untranslatedRts.length;

      if (total === 0) {
        bulkTransList.innerHTML = '<div class="admin-empty" style="margin-top: 1rem;">Everything is translated for this language.</div>';
        return;
      }

      let html = '';

      if (untranslated.length > 0) {
        html += `<p class="admin-section__desc" style="margin-top: 1rem;">${untranslated.length} concept${untranslated.length !== 1 ? 's' : ''} need translation.</p>
        <div class="admin-bulk-list" id="bulk-items"></div>`;
      }

      if (untranslatedRts.length > 0) {
        html += `<p class="admin-section__desc" style="margin-top: 1.5rem;">${untranslatedRts.length} relation type${untranslatedRts.length !== 1 ? 's' : ''} need translation.</p>
        <div class="admin-bulk-list" id="bulk-rt-items"></div>`;
      }

      bulkTransList.innerHTML = html;

      // Concept rows
      const bulkItems = document.getElementById('bulk-items');
      if (bulkItems) {
        for (const concept of untranslated) {
          const row = document.createElement('div');
          row.className = 'admin-bulk-row';
          row.innerHTML = `
            <div class="admin-bulk-row__title">${concept.title}</div>
            <form class="admin-bulk-row__form" data-slug="${concept.slug}">
              <input class="input" type="text" name="title" placeholder="Translated title..." required>
              <button type="submit" class="btn btn--signal btn--small">Save</button>
            </form>
            <div class="admin-section__feedback admin-bulk-row__feedback"></div>
          `;
          bulkItems.appendChild(row);

          row.querySelector('form').addEventListener('submit', async (e) => {
            e.preventDefault();
            const title = e.target.title.value.trim();
            const feedback = row.querySelector('.admin-bulk-row__feedback');
            try {
              await api.setConceptTranslation(concept.slug, lang.toLowerCase(), { title });
              feedback.textContent = 'Saved';
              feedback.className = 'admin-section__feedback admin-bulk-row__feedback admin-success';
              row.classList.add('admin-bulk-row--done');
            } catch (err) {
              feedback.textContent = err.message;
              feedback.className = 'admin-section__feedback admin-bulk-row__feedback form-error';
            }
          });
        }
      }

      // Relation type rows
      const bulkRtItems = document.getElementById('bulk-rt-items');
      if (bulkRtItems) {
        for (const rt of untranslatedRts) {
          const row = document.createElement('div');
          row.className = 'admin-bulk-row';
          row.innerHTML = `
            <div class="admin-bulk-row__title">${rt.name}${rt.reverse_name ? ` / ${rt.reverse_name}` : ''}</div>
            <form class="admin-bulk-row__form" data-rt-id="${rt.id}">
              <input class="input" type="text" name="name" placeholder="Translated name..." required>
              <input class="input" type="text" name="reverse_name" placeholder="Translated reverse...">
              <button type="submit" class="btn btn--signal btn--small">Save</button>
            </form>
            <div class="admin-section__feedback admin-bulk-row__feedback"></div>
          `;
          bulkRtItems.appendChild(row);

          row.querySelector('form').addEventListener('submit', async (e) => {
            e.preventDefault();
            const name = e.target.name.value.trim();
            const reverse_name = e.target.reverse_name.value.trim() || undefined;
            const feedback = row.querySelector('.admin-bulk-row__feedback');
            try {
              await api.setRelationTypeTranslation(rt.id, lang.toLowerCase(), { name, reverse_name });
              feedback.textContent = 'Saved';
              feedback.className = 'admin-section__feedback admin-bulk-row__feedback admin-success';
              row.classList.add('admin-bulk-row--done');
            } catch (err) {
              feedback.textContent = err.message;
              feedback.className = 'admin-section__feedback admin-bulk-row__feedback form-error';
            }
          });
        }
      }
    } catch (err) {
      bulkTransList.innerHTML = `<div class="form-error">${err.message}</div>`;
    }
  });

  function showTransFeedback(id, message, success) {
    const el = document.getElementById(id);
    el.textContent = message;
    el.className = `admin-section__feedback ${success ? 'admin-success' : 'form-error'}`;
  }

  makeCollapsible(panel);
}

// =====================================================================
// Analytics tab
// =====================================================================

function countryFlag(code) {
  if (!code || code.length !== 2) return '';
  return String.fromCodePoint(
    ...code.toUpperCase().split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
  );
}

const COUNTRY_NAMES = {
  US: 'United States', CN: 'China', GB: 'United Kingdom', DE: 'Germany',
  FR: 'France', JP: 'Japan', IN: 'India', BR: 'Brazil', CA: 'Canada',
  AU: 'Australia', KR: 'South Korea', NL: 'Netherlands', RU: 'Russia',
  IT: 'Italy', ES: 'Spain', SE: 'Sweden', MX: 'Mexico', SG: 'Singapore',
  HK: 'Hong Kong', TW: 'Taiwan', PH: 'Philippines', TH: 'Thailand',
  ID: 'Indonesia', MY: 'Malaysia', VN: 'Vietnam', PL: 'Poland',
  UA: 'Ukraine', TR: 'Turkey', AR: 'Argentina', CL: 'Chile', CO: 'Colombia',
  ZA: 'South Africa', NG: 'Nigeria', EG: 'Egypt', IL: 'Israel', AE: 'UAE',
  SA: 'Saudi Arabia', PK: 'Pakistan', BD: 'Bangladesh', NZ: 'New Zealand',
  IE: 'Ireland', CH: 'Switzerland', AT: 'Austria', BE: 'Belgium',
  DK: 'Denmark', FI: 'Finland', NO: 'Norway', PT: 'Portugal', CZ: 'Czechia',
  RO: 'Romania', HU: 'Hungary', GR: 'Greece',
};

async function renderAnalyticsTab(panel) {
  let currentPeriod = '7d';

  async function loadAnalytics() {
    panel.innerHTML = '<div class="loading">Loading analytics...</div>';

    try {
      const data = await api.getAnalytics(currentPeriod);

      panel.innerHTML = `
        <div class="admin-section">
          <h2 class="admin-section__title">Visitor Overview</h2>
          <div class="analytics-period-filter">
            ${['today', '7d', '30d', 'all'].map(p => `
              <button class="analytics-period-btn ${currentPeriod === p ? 'analytics-period-btn--active' : ''}" data-period="${p}">
                ${p === 'today' ? 'Today' : p === '7d' ? '7 days' : p === '30d' ? '30 days' : 'All time'}
              </button>
            `).join('')}
          </div>
          <div class="dashboard-stats">
            <div class="dashboard-stat">
              <div class="dashboard-stat__value">${data.totalViews.toLocaleString()}</div>
              <div class="dashboard-stat__label">Page Views</div>
            </div>
            <div class="dashboard-stat">
              <div class="dashboard-stat__value">${data.uniqueVisitors.toLocaleString()}</div>
              <div class="dashboard-stat__label">Unique Visitors</div>
            </div>
            <div class="dashboard-stat">
              <div class="dashboard-stat__value">${data.countries.length}</div>
              <div class="dashboard-stat__label">Countries</div>
            </div>
          </div>
        </div>

        <div class="admin-section">
          <h2 class="admin-section__title">Visitors by Country</h2>
          <div id="analytics-countries"></div>
        </div>

        <div class="admin-section">
          <h2 class="admin-section__title">Top Pages</h2>
          <div id="analytics-paths"></div>
        </div>
      `;

      // Wire up period filter
      for (const btn of panel.querySelectorAll('.analytics-period-btn')) {
        btn.addEventListener('click', () => {
          currentPeriod = btn.dataset.period;
          loadAnalytics();
        });
      }

      // Country table
      const countriesEl = panel.querySelector('#analytics-countries');
      if (data.countries.length === 0) {
        countriesEl.innerHTML = '<div class="admin-empty">No geographic data yet.</div>';
      } else {
        const total = data.totalViews || 1;
        countriesEl.innerHTML = `
          <table class="analytics-table">
            <thead>
              <tr>
                <th>Country</th>
                <th>Visitors</th>
                <th>Views</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              ${data.countries.map(c => {
                const pct = ((c.views / total) * 100).toFixed(1);
                const name = COUNTRY_NAMES[c.country_code] || c.country_code;
                const flag = countryFlag(c.country_code);
                return `<tr>
                  <td>${flag} ${name}</td>
                  <td>${c.visitors.toLocaleString()}</td>
                  <td>${c.views.toLocaleString()}</td>
                  <td>
                    <div class="analytics-bar-wrapper">
                      <div class="analytics-bar" style="width: ${pct}%"></div>
                      <span>${pct}%</span>
                    </div>
                  </td>
                </tr>`;
              }).join('')}
            </tbody>
          </table>
        `;
      }

      // Top paths table
      const pathsEl = panel.querySelector('#analytics-paths');
      if (data.topPaths.length === 0) {
        pathsEl.innerHTML = '<div class="admin-empty">No page view data yet.</div>';
      } else {
        pathsEl.innerHTML = `
          <table class="analytics-table">
            <thead>
              <tr>
                <th>Path</th>
                <th>Views</th>
              </tr>
            </thead>
            <tbody>
              ${data.topPaths.map(p => `<tr>
                <td class="analytics-path">${p.path}</td>
                <td>${p.views.toLocaleString()}</td>
              </tr>`).join('')}
            </tbody>
          </table>
        `;
      }

      makeCollapsible(panel);

    } catch (err) {
      panel.innerHTML = `<div class="admin-section"><div class="form-error">Failed to load analytics: ${err.message}</div></div>`;
    }
  }

  loadAnalytics();
}

// =====================================================================
// Tags tab
// =====================================================================
function renderTagsTab(panel, initialTags, concepts) {
  let allTags = initialTags;

  // --- Tag CRUD section ---
  const crudSection = document.createElement('div');
  crudSection.className = 'admin-section';
  crudSection.innerHTML = `
    <h2 class="admin-section__title">Manage Tags</h2>
    <form id="add-tag-form" style="display: flex; gap: 0.5rem; align-items: flex-end; margin-bottom: 1rem;">
      <div style="flex: 1;">
        <label class="label">name</label>
        <input class="input" type="text" id="tag-name-input" placeholder="e.g. Physics" required>
      </div>
      <div style="display: flex; align-items: flex-end; gap: 0.5rem;">
        <div style="flex: 1; min-width: 120px;">
          <label class="label">hue</label>
          <input type="range" id="tag-hue-input" min="0" max="360" value="15" class="hue-slider">
        </div>
        <div id="tag-hue-preview" class="hue-preview"></div>
      </div>
      <button type="submit" class="btn btn--signal btn--small">Add tag</button>
    </form>
    <div class="admin-section__feedback" id="tag-crud-feedback"></div>
    <div id="tag-list"></div>
  `;
  panel.appendChild(crudSection);

  // --- Assign tags to concepts section ---
  const assignSection = document.createElement('div');
  assignSection.className = 'admin-section';
  assignSection.innerHTML = `
    <h2 class="admin-section__title">Assign Tags to Concepts</h2>
    <div class="form-group">
      <label class="label">concept</label>
      <select class="admin-select" id="tag-concept-select">
        <option value="">select a concept...</option>
        ${concepts.map(c => `<option value="${c.slug}" data-slug="${c.slug}" data-aliases="${(c.aliases||[]).join('|')}">${c.title}</option>`).join('')}
      </select>
    </div>
    <div id="concept-tags-display" style="margin: 0.75rem 0;"></div>
    <div class="form-group">
      <label class="label">add tag</label>
      <select class="admin-select" id="tag-assign-select" disabled>
        <option value="">select a tag...</option>
      </select>
    </div>
    <div class="admin-section__feedback" id="tag-assign-feedback"></div>
  `;
  panel.appendChild(assignSection);

  function showFeedback(id, message, success) {
    const el = document.getElementById(id);
    el.textContent = message;
    el.className = `admin-section__feedback ${success ? 'admin-success' : 'form-error'}`;
  }

  // Render tag list
  let editingTagId = null;

  function renderTagList() {
    const list = document.getElementById('tag-list');
    if (!allTags.length) {
      list.innerHTML = '<div class="admin-empty">No tags yet.</div>';
      return;
    }
    list.innerHTML = allTags.map(tag => {
      if (editingTagId === tag.id) {
        const hue = tag.hue !== undefined ? tag.hue : 0;
        return `
          <div class="admin-tag-item admin-tag-item--editing" data-tag-id="${tag.id}">
            <div class="admin-tag-item__edit-form">
              <div style="flex: 1;">
                <input class="input" type="text" data-edit-name="${tag.id}" value="${tag.name}">
              </div>
              <div style="display: flex; align-items: center; gap: 0.5rem; flex: 1; min-width: 140px;">
                <input type="range" min="0" max="360" value="${hue}" class="hue-slider" data-edit-hue="${tag.id}">
                <div class="hue-preview" data-edit-preview="${tag.id}" style="background-color: hsl(${hue}, 55%, 55%);"></div>
              </div>
              <div class="admin-tag-item__actions">
                <button class="btn btn--small btn--signal save-tag-btn" data-id="${tag.id}">Save</button>
                <button class="btn btn--small cancel-tag-btn" data-id="${tag.id}">Cancel</button>
              </div>
            </div>
          </div>`;
      }
      return `
        <div class="admin-tag-item" data-tag-id="${tag.id}">
          <div class="admin-tag-item__info">
            <div class="admin-tag-item__color" style="background-color: ${tag.color};"></div>
            <span class="admin-tag-item__name">${tag.name}</span>
            <span class="admin-tag-item__hex">${tag.hue !== undefined ? tag.hue + '\u00B0' : ''}</span>
          </div>
          <div class="admin-tag-item__actions">
            <button class="btn btn--small edit-tag-btn" data-id="${tag.id}">Edit</button>
            <button class="btn btn--small btn--danger delete-tag-btn" data-id="${tag.id}">Delete</button>
          </div>
        </div>`;
    }).join('');

    // Edit button → switch to inline edit mode
    for (const btn of list.querySelectorAll('.edit-tag-btn')) {
      btn.addEventListener('click', () => {
        editingTagId = parseInt(btn.dataset.id);
        renderTagList();
      });
    }

    // Cancel button
    for (const btn of list.querySelectorAll('.cancel-tag-btn')) {
      btn.addEventListener('click', () => {
        editingTagId = null;
        renderTagList();
      });
    }

    // Save button
    for (const btn of list.querySelectorAll('.save-tag-btn')) {
      btn.addEventListener('click', async () => {
        const id = parseInt(btn.dataset.id);
        const nameInput = list.querySelector(`[data-edit-name="${id}"]`);
        const hueInput = list.querySelector(`[data-edit-hue="${id}"]`);
        const newName = nameInput.value.trim();
        const hueVal = parseInt(hueInput.value);
        if (!newName) {
          showFeedback('tag-crud-feedback', 'Name is required', false);
          return;
        }
        try {
          await api.updateTag(id, { name: newName, hue: hueVal });
          allTags = await api.getTags();
          editingTagId = null;
          renderTagList();
          showFeedback('tag-crud-feedback', `Updated "${newName}"`, true);
        } catch (err) {
          showFeedback('tag-crud-feedback', err.message, false);
        }
      });
    }

    // Live hue preview on edit sliders
    for (const slider of list.querySelectorAll('[data-edit-hue]')) {
      const id = slider.dataset.editHue;
      const preview = list.querySelector(`[data-edit-preview="${id}"]`);
      slider.addEventListener('input', () => {
        preview.style.backgroundColor = `hsl(${slider.value}, 55%, 55%)`;
      });
    }

    // Delete handlers
    for (const btn of list.querySelectorAll('.delete-tag-btn')) {
      btn.addEventListener('click', () => {
        const tag = allTags.find(t => t.id === parseInt(btn.dataset.id));
        if (!tag) return;
        if (!confirm(`Delete tag "${tag.name}"?`)) return;
        api.deleteTag(tag.id)
          .then(async () => {
            allTags = await api.getTags();
            renderTagList();
            showFeedback('tag-crud-feedback', `Deleted "${tag.name}"`, true);
          })
          .catch(err => showFeedback('tag-crud-feedback', err.message, false));
      });
    }
  }

  renderTagList();

  // Add tag form
  document.getElementById('add-tag-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = document.getElementById('tag-name-input').value.trim();
    const hue = parseInt(document.getElementById('tag-hue-input').value);
    try {
      await api.createTag(name, hue);
      allTags = await api.getTags();
      renderTagList();
      document.getElementById('tag-name-input').value = '';
      showFeedback('tag-crud-feedback', `Created "${name}"`, true);
    } catch (err) {
      showFeedback('tag-crud-feedback', err.message, false);
    }
  });

  // Hue slider preview
  const hueSlider = document.getElementById('tag-hue-input');
  const huePreview = document.getElementById('tag-hue-preview');
  function updateHuePreview() {
    const h = hueSlider.value;
    huePreview.style.backgroundColor = `hsl(${h}, 55%, 55%)`;
  }
  hueSlider.addEventListener('input', updateHuePreview);
  updateHuePreview();

  // Concept tag assignment
  const conceptSelect = document.getElementById('tag-concept-select');
  const assignSelect = document.getElementById('tag-assign-select');
  const tagsDisplay = document.getElementById('concept-tags-display');

  async function loadConceptTags(slug) {
    if (!slug) {
      tagsDisplay.innerHTML = '';
      assignSelect.disabled = true;
      if (assignWrapper) assignWrapper.querySelector('.search-select__input').disabled = true;
      return;
    }

    // Find the concept's tags from the concepts data
    const concept = concepts.find(c => c.slug === slug);
    const conceptTags = concept?.tags || [];

    tagsDisplay.innerHTML = conceptTags.length
      ? conceptTags.map(tag => `
        <span class="tag-badge" style="background-color: ${tag.color}; color: #fff;">
          ${tag.name}
          <button class="tag-remove-btn" data-slug="${slug}" data-tag-id="${tag.id}">&times;</button>
        </span>
      `).join('')
      : '<span style="color: var(--dim); font-size: 0.85rem;">no tags</span>';

    // Update assign dropdown (exclude already assigned)
    const assignedIds = new Set(conceptTags.map(t => t.id));
    const available = allTags.filter(t => !assignedIds.has(t.id));
    assignSelect.innerHTML = `
      <option value="">select a tag...</option>
      ${available.map(t => `<option value="${t.id}">${t.name}</option>`).join('')}
    `;
    assignSelect.disabled = false;
    assignSelect.value = '';
    if (assignWrapper) {
      const inp = assignWrapper.querySelector('.search-select__input');
      inp.disabled = false;
      inp.value = '';
      assignWrapper._refresh();
    }

    // Remove handlers
    for (const btn of tagsDisplay.querySelectorAll('.tag-remove-btn')) {
      btn.addEventListener('click', async () => {
        try {
          await api.removeTagFromConcept(btn.dataset.slug, btn.dataset.tagId);
          // Update local concept tags
          const c = concepts.find(c => c.slug === slug);
          if (c) c.tags = (c.tags || []).filter(t => t.id !== parseInt(btn.dataset.tagId));
          loadConceptTags(slug);
        } catch (err) {
          showFeedback('tag-assign-feedback', err.message, false);
        }
      });
    }
  }

  conceptSelect.addEventListener('change', () => loadConceptTags(conceptSelect.value));

  assignSelect.addEventListener('change', async () => {
    const tagId = assignSelect.value;
    const slug = conceptSelect.value;
    if (!tagId || !slug) return;
    try {
      const tag = await api.assignTagToConcept(slug, tagId);
      // Update local concept tags
      const c = concepts.find(c => c.slug === slug);
      if (c) {
        if (!c.tags) c.tags = [];
        c.tags.push(tag);
      }
      loadConceptTags(slug);
      showFeedback('tag-assign-feedback', `Added tag`, true);
    } catch (err) {
      showFeedback('tag-assign-feedback', err.message, false);
    }
  });

  // Upgrade selects to search selects
  upgradeToSearchSelect(conceptSelect);
  const assignWrapper = upgradeToSearchSelect(assignSelect);
  // Start assign input as disabled (no concept selected yet)
  if (assignWrapper) assignWrapper.querySelector('.search-select__input').disabled = true;

  // --- Quick Tag section ---
  const quickSection = document.createElement('div');
  quickSection.className = 'admin-section';
  quickSection.innerHTML = `
    <h2 class="admin-section__title">Quick Tag</h2>
    <p style="color: var(--dim); font-size: 0.8rem; margin-bottom: 0.75rem;">Click tags to toggle them on untagged concepts.</p>
    <div class="form-group" style="margin-bottom: 0.75rem;">
      <label class="label" style="display: flex; align-items: center; gap: 0.5rem;">
        <input type="checkbox" id="quick-tag-show-all"> show all concepts
      </label>
    </div>
    <div id="quick-tag-list"></div>
    <div class="admin-section__feedback" id="quick-tag-feedback"></div>
  `;
  panel.appendChild(quickSection);

  function renderQuickTagList() {
    const container = document.getElementById('quick-tag-list');
    const showAll = document.getElementById('quick-tag-show-all')?.checked;
    const filtered = showAll
      ? [...concepts].sort((a, b) => a.title.localeCompare(b.title))
      : concepts.filter(c => !c.tags || c.tags.length === 0).sort((a, b) => a.title.localeCompare(b.title));

    if (!filtered.length) {
      container.innerHTML = '<div class="admin-empty">All concepts are tagged!</div>';
      return;
    }
    if (!allTags.length) {
      container.innerHTML = '<div class="admin-empty">Create some tags first.</div>';
      return;
    }

    container.innerHTML = filtered.map(c => {
      const assignedIds = new Set((c.tags || []).map(t => t.id));
      return `
        <div class="quick-tag-row" data-slug="${c.slug}">
          <span class="quick-tag-row__title">${c.title} <span class="quick-tag-row__slug">${c.slug}</span></span>
          <div class="quick-tag-row__tags">
            ${allTags.map(tag => `
              <button class="quick-tag-btn ${assignedIds.has(tag.id) ? 'quick-tag-btn--active' : ''}"
                data-slug="${c.slug}" data-tag-id="${tag.id}" data-tag-name="${tag.name}"
                style="--tag-btn-color: ${tag.color};"
                title="${tag.name}">${tag.name}</button>
            `).join('')}
          </div>
        </div>`;
    }).join('');

    // Toggle handlers
    for (const btn of container.querySelectorAll('.quick-tag-btn')) {
      btn.addEventListener('click', async () => {
        const slug = btn.dataset.slug;
        const tagId = parseInt(btn.dataset.tagId);
        const isActive = btn.classList.contains('quick-tag-btn--active');
        btn.disabled = true;
        try {
          if (isActive) {
            await api.removeTagFromConcept(slug, tagId);
            const c = concepts.find(c => c.slug === slug);
            if (c) c.tags = (c.tags || []).filter(t => t.id !== tagId);
          } else {
            const tag = await api.assignTagToConcept(slug, tagId);
            const c = concepts.find(c => c.slug === slug);
            if (c) {
              if (!c.tags) c.tags = [];
              c.tags.push(tag);
            }
          }
          renderQuickTagList();
          // Also refresh the assign section if same concept is selected
          if (conceptSelect.value === slug) loadConceptTags(slug);
        } catch (err) {
          showFeedback('quick-tag-feedback', err.message, false);
        }
        btn.disabled = false;
      });
    }
  }

  renderQuickTagList();

  document.getElementById('quick-tag-show-all')?.addEventListener('change', renderQuickTagList);

  makeCollapsible(panel);
}

// =====================================================================
// Flows tab
// =====================================================================
function renderFlowsTab(panel, concepts) {
  let selectedSlug = null;

  function showFeedback(id, msg, success) {
    const el = document.getElementById(id);
    if (!el) return;
    el.textContent = msg;
    el.className = success ? 'admin-section__feedback admin-success' : 'admin-section__feedback form-error';
    if (success) setTimeout(() => { el.textContent = ''; el.className = 'admin-section__feedback'; }, 2000);
  }

  function renderPanel() {
    panel.innerHTML = `
      <div class="admin-section">
        <h2 class="admin-section__title">Flow Editor</h2>
        <p style="color: var(--bone); font-size: 0.85rem; margin-bottom: 1rem;">
          Create procedure flowcharts for concepts. Select a concept to manage its flow steps and edges.
        </p>
        <div class="form-group">
          <label class="form-group__label">Concept</label>
          <select class="admin-select" id="flow-concept-select">
            <option value="">select concept...</option>
            ${concepts.map(c => `<option value="${c.slug}" data-aliases="${(c.aliases||[]).join('|')}" ${c.slug === selectedSlug ? 'selected' : ''}>${c.title}</option>`).join('')}
          </select>
        </div>
      </div>
      <div id="flow-editor-area"></div>
    `;

    const select = document.getElementById('flow-concept-select');
    select.addEventListener('change', () => {
      selectedSlug = select.value || null;
      if (selectedSlug) {
        loadFlowEditor(selectedSlug);
      } else {
        document.getElementById('flow-editor-area').innerHTML = '';
      }
    });

    if (selectedSlug) loadFlowEditor(selectedSlug);
  }

  async function loadFlowEditor(slug) {
    const area = document.getElementById('flow-editor-area');
    area.innerHTML = '<div class="loading">Loading flow...</div>';

    try {
      const flowData = await api.getFlow(slug);
      const steps = flowData.steps || [];
      const edges = flowData.edges || [];

      area.innerHTML = `
        <div class="admin-section">
          <h2 class="admin-section__title">Steps (${steps.length})</h2>
          <div id="flow-steps-list"></div>
          <div style="margin-top: 1rem; padding: 1rem; background: var(--off); border-radius: var(--radius-button);">
            <h3 style="font-family: var(--font-system); font-size: 0.85rem; color: var(--bone); margin-bottom: 0.75rem;">Add Step</h3>
            <div style="display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: end;">
              <div class="form-group" style="flex: 1; min-width: 150px;">
                <label class="form-group__label">Label</label>
                <input class="input" id="flow-step-label" placeholder="Step label..." />
              </div>
              <div class="form-group">
                <label class="form-group__label">Shape</label>
                <select class="admin-select" id="flow-step-shape">
                  <option value="process">process</option>
                  <option value="start">start</option>
                  <option value="decision">decision</option>
                  <option value="end">end</option>
                </select>
              </div>
              <div class="form-group" style="width: 80px;">
                <label class="form-group__label">Order</label>
                <input class="input" id="flow-step-order" type="number" value="0" />
              </div>
              <button class="btn btn--signal btn--small" id="flow-add-step-btn">Add</button>
            </div>
            <div class="admin-section__feedback" id="flow-step-feedback"></div>
          </div>
        </div>

        <div class="admin-section" style="margin-top: 1.5rem;">
          <h2 class="admin-section__title">Edges (${edges.length})</h2>
          <div id="flow-edges-list"></div>
          ${steps.length >= 2 ? `
          <div style="margin-top: 1rem; padding: 1rem; background: var(--off); border-radius: var(--radius-button);">
            <h3 style="font-family: var(--font-system); font-size: 0.85rem; color: var(--bone); margin-bottom: 0.75rem;">Add Edge</h3>
            <div style="display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: end;">
              <div class="form-group" style="flex: 1; min-width: 120px;">
                <label class="form-group__label">From</label>
                <select class="admin-select" id="flow-edge-from">
                  ${steps.map(s => `<option value="${s.id}">${s.label} (${s.shape})</option>`).join('')}
                </select>
              </div>
              <div class="form-group" style="flex: 1; min-width: 120px;">
                <label class="form-group__label">To</label>
                <select class="admin-select" id="flow-edge-to">
                  ${steps.map(s => `<option value="${s.id}">${s.label} (${s.shape})</option>`).join('')}
                </select>
              </div>
              <div class="form-group" style="flex: 1; min-width: 100px;">
                <label class="form-group__label">Label (optional)</label>
                <input class="input" id="flow-edge-label" placeholder="e.g. yes, no..." />
              </div>
              <button class="btn btn--signal btn--small" id="flow-add-edge-btn">Add</button>
            </div>
            <div class="admin-section__feedback" id="flow-edge-feedback"></div>
          </div>
          ` : '<p style="color: var(--dim); font-size: 0.85rem;">Add at least 2 steps to create edges.</p>'}
        </div>

        <div class="admin-section" style="margin-top: 1.5rem;">
          <h2 class="admin-section__title">Preview</h2>
          <div id="flow-preview-container" style="height: 400px; background: var(--black); border-radius: var(--radius-card); overflow: hidden;"></div>
        </div>
      `;

      // Render steps list
      const stepsList = document.getElementById('flow-steps-list');
      if (steps.length === 0) {
        stepsList.innerHTML = '<p style="color: var(--dim); font-size: 0.85rem;">No steps yet.</p>';
      } else {
        stepsList.innerHTML = steps.map(s => `
          <div class="admin-flow-item" data-step-id="${s.id}">
            <span class="admin-flow-item__label">${s.label}</span>
            <span class="flow-shape-badge flow-shape-badge--${s.shape}">${s.shape}</span>
            <span class="admin-flow-item__order">#${s.sort_order}</span>
            <button class="btn btn--ghost btn--small admin-flow-item__edit" data-action="edit-step" data-id="${s.id}">edit</button>
            <button class="btn btn--danger btn--small admin-flow-item__delete" data-action="delete-step" data-id="${s.id}">delete</button>
          </div>
        `).join('');
      }

      // Render edges list
      const edgesList = document.getElementById('flow-edges-list');
      if (edges.length === 0) {
        edgesList.innerHTML = '<p style="color: var(--dim); font-size: 0.85rem;">No edges yet.</p>';
      } else {
        const stepById = (id) => steps.find(s => s.id === id);
        edgesList.innerHTML = edges.map(e => {
          const from = stepById(e.from_step_id);
          const to = stepById(e.to_step_id);
          return `
            <div class="admin-flow-item" data-edge-id="${e.id}">
              <span class="admin-flow-item__label">${from ? from.label : '?'} &rarr; ${to ? to.label : '?'}${e.label ? ` <span style="color: var(--bone);">[${e.label}]</span>` : ''}</span>
              <button class="btn btn--ghost btn--small admin-flow-item__edit" data-action="edit-edge" data-id="${e.id}">edit</button>
              <button class="btn btn--danger btn--small admin-flow-item__delete" data-action="delete-edge" data-id="${e.id}">delete</button>
            </div>
          `;
        }).join('');
      }

      // Attach [[link]] autocomplete to step label input
      const stepLabelInput = document.getElementById('flow-step-label');
      if (stepLabelInput) {
        attachConceptAutocomplete(stepLabelInput, concepts);
      }

      // Event: Add step
      document.getElementById('flow-add-step-btn')?.addEventListener('click', async () => {
        const label = document.getElementById('flow-step-label').value.trim();
        const shape = document.getElementById('flow-step-shape').value;
        const sort_order = parseInt(document.getElementById('flow-step-order').value) || 0;
        if (!label) {
          showFeedback('flow-step-feedback', 'Label is required.', false);
          return;
        }
        try {
          await api.createFlowStep(slug, { label, shape, sort_order });
          loadFlowEditor(slug);
        } catch (err) {
          showFeedback('flow-step-feedback', err.message, false);
        }
      });

      // Event: Add edge
      document.getElementById('flow-add-edge-btn')?.addEventListener('click', async () => {
        const from_step_id = parseInt(document.getElementById('flow-edge-from').value);
        const to_step_id = parseInt(document.getElementById('flow-edge-to').value);
        const label = document.getElementById('flow-edge-label').value.trim() || null;
        if (from_step_id === to_step_id) {
          showFeedback('flow-edge-feedback', 'From and To must be different steps.', false);
          return;
        }
        try {
          await api.createFlowEdge(slug, { from_step_id, to_step_id, label });
          loadFlowEditor(slug);
        } catch (err) {
          showFeedback('flow-edge-feedback', err.message, false);
        }
      });

      // Event delegation for edit/delete
      area.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;
        const action = btn.dataset.action;
        const id = parseInt(btn.dataset.id);

        if (action === 'delete-step') {
          if (!confirm('Delete this step? Edges connected to it will also be deleted.')) return;
          try {
            await api.deleteFlowStep(id);
            loadFlowEditor(slug);
          } catch (err) {
            alert('Failed to delete: ' + err.message);
          }
        } else if (action === 'delete-edge') {
          if (!confirm('Delete this edge?')) return;
          try {
            await api.deleteFlowEdge(id);
            loadFlowEditor(slug);
          } catch (err) {
            alert('Failed to delete: ' + err.message);
          }
        } else if (action === 'edit-step') {
          const step = steps.find(s => s.id === id);
          if (!step) return;
          const row = btn.closest('.admin-flow-item');
          if (!row) return;
          // Replace the row with an inline edit form
          row.innerHTML = `
            <div style="display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: end; flex: 1;">
              <div class="form-group" style="flex: 1; min-width: 150px;">
                <label class="form-group__label">Label</label>
                <input class="input edit-step-label" value="${step.label.replace(/"/g, '&quot;')}" />
              </div>
              <div class="form-group">
                <label class="form-group__label">Shape</label>
                <select class="admin-select edit-step-shape">
                  ${['start', 'process', 'decision', 'end'].map(s => `<option value="${s}" ${s === step.shape ? 'selected' : ''}>${s}</option>`).join('')}
                </select>
              </div>
              <div class="form-group" style="width: 80px;">
                <label class="form-group__label">Order</label>
                <input class="input edit-step-order" type="number" value="${step.sort_order}" />
              </div>
              <button class="btn btn--signal btn--small edit-step-save">save</button>
              <button class="btn btn--ghost btn--small edit-step-cancel">cancel</button>
            </div>
          `;
          const editLabelInput = row.querySelector('.edit-step-label');
          attachConceptAutocomplete(editLabelInput, concepts);
          editLabelInput.focus();

          row.querySelector('.edit-step-save').addEventListener('click', async () => {
            const newLabel = editLabelInput.value.trim();
            const newShape = row.querySelector('.edit-step-shape').value;
            const newOrder = parseInt(row.querySelector('.edit-step-order').value) || 0;
            if (!newLabel) { alert('Label is required.'); return; }
            const validShapes = ['start', 'process', 'decision', 'end'];
            if (!validShapes.includes(newShape)) { alert('Invalid shape.'); return; }
            try {
              await api.updateFlowStep(id, { label: newLabel, shape: newShape, sort_order: newOrder });
              loadFlowEditor(slug);
            } catch (err) {
              alert('Failed to update: ' + err.message);
            }
          });
          row.querySelector('.edit-step-cancel').addEventListener('click', () => loadFlowEditor(slug));
        } else if (action === 'edit-edge') {
          const edge = edges.find(e => e.id === id);
          if (!edge) return;
          const newLabel = prompt('Edge label (leave empty for none):', edge.label || '');
          if (newLabel === null) return;
          try {
            await api.updateFlowEdge(id, { label: newLabel.trim() || null });
            loadFlowEditor(slug);
          } catch (err) {
            alert('Failed to update: ' + err.message);
          }
        }
      });

      // Render preview
      if (steps.length > 0) {
        const previewContainer = document.getElementById('flow-preview-container');
        // Build conceptMap for [[slug]] title resolution
        const conceptMap = new Map();
        for (const c of concepts) conceptMap.set(c.slug, c.title);
        import('../components/flowRenderer.js').then(({ createFlowRenderer }) => {
          createFlowRenderer(previewContainer, { steps, edges }, { conceptMap });
        });
      }

    } catch (err) {
      area.innerHTML = `<p class="form-error">Failed to load flow: ${err.message}</p>`;
    }
  }

  renderPanel();
  makeCollapsible(panel);
}
